import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box
} from '@mui/material';
import { Add, Save, Cancel } from '@mui/icons-material';
import { useNoteForm } from './hooks';

interface AddNoteDialogProps {
  open: boolean;
  onClose: () => void;
}

const AddNoteDialog: React.FC<AddNoteDialogProps> = ({ open, onClose }) => {
  const { noteText, setNoteText, isValid, handleSave, handleReset } = useNoteForm(onClose);

  const handleClose = () => {
    handleReset();
    onClose();
  };

  const handleSubmit = () => {
    handleSave();
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && event.ctrlKey) {
      handleSubmit();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
        }
      }}
    >
      <DialogTitle
        sx={{
          pb: 2,
          borderBottom: '1px solid #e2e8f0',
          backgroundColor: '#f8fafc',
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}
      >
        <Add sx={{ color: '#3b82f6' }} />
        Add New Note
      </DialogTitle>

      <DialogContent sx={{ pt: 3, pb: 2 }}>
        <TextField
          autoFocus
          label="Note Text"
          multiline
          rows={4}
          fullWidth
          variant="outlined"
          value={noteText}
          onChange={(e) => setNoteText(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Enter your note here... (Ctrl+Enter to save)"
          InputProps={{
            style: {
              alignItems: 'flex-start',
              padding: 0
            }
          }}
          inputProps={{
            style: {
              padding: '14px',
              minHeight: 'unset'
            }
          }}
          sx={{
            mt: 2,
            '& .MuiOutlinedInput-root': {
              backgroundColor: '#ffffff',
              '&:hover': {
                backgroundColor: '#f8fafc'
              },
              '&.Mui-focused': {
                backgroundColor: '#ffffff'
              }
            }
          }}
        />

        <Box sx={{ mt: 1, fontSize: '0.875rem', color: '#64748b' }}>
          Tip: Use Ctrl+Enter to quickly save the note
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          p: 3,
          borderTop: '1px solid #e2e8f0',
          backgroundColor: '#f8fafc',
          gap: 1
        }}
      >
        <Button
          onClick={handleClose}
          variant="outlined"
          startIcon={<Cancel />}
          sx={{
            borderColor: '#d1d5db',
            color: '#6b7280',
            '&:hover': {
              borderColor: '#9ca3af',
              backgroundColor: '#f9fafb'
            }
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          startIcon={<Save />}
          disabled={!isValid}
          sx={{
            backgroundColor: '#162346',
            '&:hover': {
              backgroundColor: '#1a2a52'
            },
            '&:disabled': {
              backgroundColor: '#e5e7eb',
              color: '#9ca3af'
            }
          }}
        >
          Save Note
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddNoteDialog;
